import { parseMeetingLink } from '../../../shared/external-meeting-links';
import { EXTERNAL_MEETING_CALL_ENDED, REQUEST_INTERPRETER, CANCEL_REQUEST, MATCH_FOUND, MEETING_INITIATED, INTERPRETED_CALL_ENDED, QUEUE_STATUS_UPDATE, QUEUE_CONNECTION_CHANGED, INTERPRETER_AVAILABILITY_CHANGED, INTERPRETER_REQUEST_RECEIVED, INTERPRETER_WRAP_UP_UPDATED, QUEUE_SERVICE_ERROR, SET_PAUSE_AFTER_CALL } from './actionTypes';
import { queueService } from './InterpreterQueueService';
import { getLocalParticipant } from '../base/participants/functions';
import { mobileLog } from '../mobile/navigation/logging';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
/**
 * Creates an action to request an interpreter.
 *
 * @param {string} language - The requested language (e.g., 'ASL').
 * @param {RequestInterpreterOptions} options - Optional service-mode and invite metadata.
 * @returns {Function}
 */
export function requestInterpreter(language = 'ASL', options = {}) {
    return (dispatch, getState) => {
        const localParticipant = getLocalParticipant(getState());
        const clientName = localParticipant?.name || localParticipant?.displayName || 'Guest';
        // Generate a unique request ID
        const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const canAuthenticate = queueService.canAuthenticate();
        mobileLog('info', 'queue_request_interpreter_action', {
            canAuthenticate,
            callType: options.callType || 'vrs',
            hasCallerIdPhone: Boolean(options.callerIdPhone),
            hasTargetPhone: Boolean(options.targetPhone),
            vcoEnabled: Boolean(options.vcoEnabled),
            queueConnected: queueService.isConnected()
        }, { console: false });
        emitMobileTelemetryEvent('queue_request_interpreter_action', {
            canAuthenticate,
            callType: options.callType || 'vrs',
            hasCallerIdPhone: Boolean(options.callerIdPhone),
            hasTargetPhone: Boolean(options.targetPhone),
            vcoEnabled: Boolean(options.vcoEnabled),
            queueConnected: queueService.isConnected()
        }, {
            requestId,
            roomName: options.roomName
        });
        if (!canAuthenticate) {
            dispatch({
                type: QUEUE_SERVICE_ERROR,
                error: 'Please sign in again before requesting an interpreter.'
            });
            return;
        }
        // Dispatch action to update local state
        const meeting = options.externalMeetingUrl ? parseMeetingLink(options.externalMeetingUrl) : null;
        const reminder = options.externalMeetingReminder || null;
        dispatch({
            type: REQUEST_INTERPRETER,
            requestId,
            language,
            clientName,
            externalMeeting: meeting?.ok ? { host: meeting.host, platform: meeting.platform }
                : reminder ? { host: reminder.host, platform: reminder.platform } : undefined
        });
        // Send request to queue service. The service buffers the message and
        // delivers it after the socket authenticates, so a cold start or an
        // in-flight token refresh does not silently drop the request.
        if (!queueService.isConnected()) {
            console.warn('Queue service not connected - request buffered until authentication completes');
        }
        const { externalMeetingReminder, ...sendOptions } = options;
        queueService.requestInterpreter(language, clientName, {
            ...sendOptions,
            externalMeetingReminderId: externalMeetingReminder?.id || undefined
        });
    };
}
/**
 * Ends interpreting in the client's own meeting (there is no room to leave).
 *
 * @param {string} callId - The external-meeting call.
 * @param {string} [roomName] - Its server room name, if known.
 * @returns {Function}
 */
export function endExternalMeetingCall(callId, roomName) {
    return (dispatch) => {
        queueService.endCall(callId, roomName);
        dispatch({ type: EXTERNAL_MEETING_CALL_ENDED, callId });
    };
}
/**
 * Creates an action to cancel a pending interpreter request.
 *
 * @returns {Function}
 */
export function cancelInterpreterRequest() {
    return (dispatch, getState) => {
        const requestId = getState()['features/interpreter-queue']?.currentRequestId;
        dispatch({
            type: CANCEL_REQUEST
        });
        queueService.cancelRequest(requestId);
    };
}
/**
 * Creates an action to accept an interpreter request.
 *
 * @param {string} requestId - The ID of the request to accept.
 * @returns {Function}
 */
export function acceptInterpreterRequest(requestId) {
    return (_dispatch, getState) => {
        const state = getState();
        const request = state['features/interpreter-queue']?.pendingRequests?.find((r) => r.id === requestId);
        // Keep the offer visible until request_accepted/request_withdrawn.
        // Removing it on click hides correlated server failures and prevents a
        // safe retry after a transient or authorization error.
        if (request?.roomName) {
            queueService.acceptRequest(requestId, request.roomName, request.offerId);
        }
        else {
            queueService.acceptRequest(requestId, undefined, request?.offerId);
        }
    };
}
/**
 * Creates an action to decline an interpreter request.
 *
 * @param {string} requestId - The ID of the request to decline.
 * @returns {Function}
 */
export function declineInterpreterRequest(requestId) {
    return (_dispatch, getState) => {
        const request = getState()['features/interpreter-queue']?.pendingRequests?.find((r) => r.id === requestId);
        // The server acknowledges request_declined and the middleware removes
        // the exact card. Until then the UI remains busy instead of pretending
        // the offer was processed.
        queueService.declineRequest(requestId, request?.offerId);
    };
}
/**
 * Action dispatched when a new interpreter request is received (for interpreters).
 */
export function interpreterRequestReceived(request) {
    return {
        type: INTERPRETER_REQUEST_RECEIVED,
        request
    };
}
/**
 * Action dispatched when a match is found.
 */
export function matchFound(matchData) {
    return {
        type: MATCH_FOUND,
        matchData
    };
}
/**
 * Action dispatched when a meeting is initiated.
 */
export function meetingInitiated(meetingData) {
    return {
        type: MEETING_INITIATED,
        meetingData
    };
}
/**
 * Action dispatched when an interpreted call is no longer active.
 */
export function interpretedCallEnded(reason) {
    return {
        type: INTERPRETED_CALL_ENDED,
        reason
    };
}
/**
 * Action dispatched when queue status is updated.
 */
export function queueStatusUpdate(status) {
    return {
        type: QUEUE_STATUS_UPDATE,
        status
    };
}
/**
 * Action dispatched when queue connection status changes.
 */
export function queueConnectionChanged(connected, error) {
    return {
        type: QUEUE_CONNECTION_CHANGED,
        connected,
        error
    };
}
/**
 * The interpreter's in-call "Pause after this call" switch. Read once, when
 * the call ends, by the wrap-up (see wrapUp.ts).
 */
export function setPauseAfterCall(enabled) {
    return {
        type: SET_PAUSE_AFTER_CALL,
        enabled
    };
}
/**
 * Replace (or with undefined, end) the interpreter's wrap-up between calls.
 */
export function interpreterWrapUpUpdated(wrapUp) {
    return {
        type: INTERPRETER_WRAP_UP_UPDATED,
        wrapUp
    };
}
/**
 * The server confirmed whether this interpreter is in the queue. Drives Malka
 * Terp's keep-awake while Available.
 */
/**
 * The server's word on availability. `at` is when it arrived: the server
 * re-queues an interpreter at the back each time it confirms Available, so
 * this is when the current wait began.
 *
 * @param {boolean} available - Whether the server confirmed Available.
 * @param {number} at - When the confirmation arrived.
 * @returns {Object} The action.
 */
export function interpreterAvailabilityChanged(available, at = Date.now()) {
    return {
        type: INTERPRETER_AVAILABILITY_CHANGED,
        available,
        at
    };
}
